import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import "../styles/PartyDashboardPage.css";
import AccountOverviewCard from "../components/AccountOverviewCard";
import { fetchAccountSummary, fetchPortalProfile, fetchCatalogueDispatchHistory, fetchCataloguePerformance, fetchMonthlySalesPerformance } from "../services/api";

function formatToday() {
  return new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function TruckIcon() {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="1" y="6" width="14" height="10" rx="1" />
      <path d="M15 9h4l3 3v4h-7" />
      <circle cx="5.5" cy="18" r="1.5" />
      <circle cx="17.5" cy="18" r="1.5" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function PartyDashboardPage({ dealer, onLogout }) {
  const navigate = useNavigate();
  const [accountSummary, setAccountSummary] = useState(null);
  const [portalProfile, setPortalProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dispatches, setDispatches] = useState([]);
  const [cataloguePerformance, setCataloguePerformance] = useState(null);
  const [salesPerformance, setSalesPerformance] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const loadSummary = useCallback(async () => {
    if (!dealer?.sessionToken) {
      setLoading(false);
      setError("Unable to load account summary.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetchAccountSummary();
      setAccountSummary(response.accountSummary || null);
    } catch (err) {
      setError("Unable to load account summary.");
    } finally {
      setLoading(false);
    }
  }, [dealer]);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError("");

    fetchPortalProfile()
      .then(async (response) => {
        if (cancelled) return;
        setPortalProfile(response);
        const permissions = response?.permissions || {};
        const jobs = [];
        if (permissions.canViewOrders) jobs.push(fetchCatalogueDispatchHistory().then((data) => !cancelled && setDispatches(data.dispatches || [])));
        if (permissions.canViewAccount) {
          jobs.push(fetchCataloguePerformance().then((data) => !cancelled && setCataloguePerformance(data.performance || null)));
          jobs.push(fetchMonthlySalesPerformance().then((data) => !cancelled && setSalesPerformance(data.performance || null)));
        }
        await Promise.allSettled(jobs);

        if (permissions.canViewAccount) {
          await loadSummary();
        } else {
          setAccountSummary(null);
          setLoading(false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setPortalProfile(null);
        setLoading(false);
        setError("Unable to load portal access.");
      });

    return () => {
      cancelled = true;
    };
  }, [dealer, loadSummary, refreshKey]);

  const displaySalesMonths = [...(salesPerformance?.months || [])].reverse();

  const handleLogout = async () => {
    if (onLogout) await onLogout();
    navigate("/login");
  };

  return (
    <div className="party-dashboard-page">
      {menuOpen && (
        <button
          type="button"
          className="dealer-menu-scrim"
          aria-label="Close menu"
          onClick={() => setMenuOpen(false)}
        />
      )}
      <aside className={`dealer-sidebar${menuOpen ? " is-open" : ""}`} aria-label="Dealer menu">
        <div className="dealer-sidebar-brand">
          <span className="dealer-sidebar-logo">ABN</span>
          <span className="dealer-sidebar-caption">DEALER PORTAL</span>
        </div>
        <p className="dealer-sidebar-label">MENU</p>
        <button className="dealer-sidebar-link is-current" onClick={() => { setMenuOpen(false); navigate("/dashboard"); }}>
          <span className="dealer-sidebar-icon">⌂</span>Dashboard
        </button>
        <p className="dealer-sidebar-label dealer-sidebar-label--account">ACCOUNT</p>
        <button className="dealer-sidebar-link" onClick={() => { setMenuOpen(false); navigate("/profile"); }}>
          <span className="dealer-sidebar-icon">◉</span>Profile &amp; password
        </button>
        {portalProfile?.isAccountOwner && (
          <button className="dealer-sidebar-link" onClick={() => { setMenuOpen(false); navigate("/staff-access"); }}>
            <span className="dealer-sidebar-icon">♧</span>Manage users
          </button>
        )}
        <div className="dealer-sidebar-spacer" />
        <button className="dealer-sidebar-link dealer-sidebar-logout" onClick={handleLogout}>
          <span className="dealer-sidebar-icon">↪</span>Logout
        </button>
      </aside>
      <div className="party-dashboard-main">
      <PageHeader
        title="Party Dashboard"
        showBack={false}
        showLogo
        hideMenu
        actions={
          <>
            <button
              type="button"
              className="dealer-mobile-menu"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
            >
              <span aria-hidden="true">☰</span><span>Menu</span>
            </button>
            <button
              type="button"
              className="page-header-refresh"
              onClick={() => setRefreshKey((value) => value + 1)}
              disabled={loading}
              aria-label="Refresh dashboard data"
              title="Refresh data"
            >
              ↻
            </button>
          </>
        }
      />

      <main className="party-dashboard-content">
        <div className="party-dashboard-greeting">
          <p className="party-dashboard-welcome">
            Welcome, {dealer?.accountName}
          </p>
          <p className="party-dashboard-date">{formatToday()}</p>
        </div>

        {error ? (
          <div className="error-state">
            <button
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              {error}
            </button>
          </div>
        ) : (
          !loading &&
          portalProfile?.permissions?.canViewAccount && (
            <AccountOverviewCard
              accountSummary={accountSummary}
              onDownloadLedger={() => navigate("/ledger")}
            />
          )
        )}

        {(portalProfile?.permissions?.canViewOrders || portalProfile?.permissions?.canViewAccount) && (
          <section className="portal-insights-grid" aria-label="Sales performance and optional dispatch details">
            {portalProfile?.permissions?.canViewOrders && (
              <details className="portal-insight-card portal-insight-disclosure">
                <summary>Files Sent</summary>
                <p className="portal-insight-period">Recent catalogue dispatches</p>
                {dispatches.length ? dispatches.slice(0, 6).map((item, index) => (
                  <div className="portal-dispatch-row" key={item.dispatchDate || index}>
                    <strong>{new Date(item.dispatchDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })}</strong>
                    <span>{item.catalogues.join(", ")}</span>
                  </div>
                )) : <p className="portal-insight-empty">No recent dispatches found.</p>}
              </details>
            )}
            {portalProfile?.permissions?.canViewAccount && (
              <details className="portal-insight-card portal-insight-disclosure">
                <summary>Files Performance</summary>
                <p className="portal-insight-period">{cataloguePerformance?.periodLabel || "Last 6 months"}</p>
                {cataloguePerformance?.sold?.length ? cataloguePerformance.sold.slice(0, 5).map((item, index) => (
                  <div className="portal-performance-row" key={index}><span>{item.name}</span><strong>{item.qtyText}</strong></div>
                )) : <p className="portal-insight-empty">No catalogue sales recorded.</p>}
                {!!cataloguePerformance?.noSaleNames?.length && <p className="portal-no-sale">No sales: {cataloguePerformance.noSaleNames.slice(0, 4).join(", ")}{cataloguePerformance.noSaleNames.length > 4 ? "…" : ""}</p>}
              </details>
            )}
            {portalProfile?.permissions?.canViewAccount && (
              <article className="portal-insight-card portal-sales-card">
                <div className="portal-sales-heading">
                  <div><h2>Sales Performance</h2><p className="portal-insight-period">{salesPerformance?.financialYear ? `FY ${salesPerformance.financialYear}` : "Current financial year"}</p></div>
                  {salesPerformance?.asOfDate && <span className="portal-sales-asof">As of {salesPerformance.asOfDate}</span>}
                </div>
                <div className="portal-sales-chart" role="img" aria-label="Monthly sales before GST">
                  {displaySalesMonths.map((item) => {
                    const max = Math.max(1, ...(salesPerformance?.months || []).map((month) => Number(month.amount) || 0));
                    const amount = Number(item.amount) || 0;
                    return <div className="portal-sales-month" key={item.month} title={`${item.month}: ₹${amount.toLocaleString("en-IN")} before GST`}>
                      <span className="portal-sales-amount">₹{amount >= 100000 ? `${(amount / 100000).toFixed(1)}L` : amount.toLocaleString("en-IN")}</span>
                      <div className="portal-sales-bar-wrap"><div className="portal-sales-bar" style={{ height: `${Math.max(amount ? 5 : 0, (amount / max) * 100)}%` }} /></div>
                      <span className="portal-sales-month-label">{item.month}</span>
                    </div>;
                  })}
                </div>
                <p className="portal-insight-footnote">Monthly sales amount before GST</p>
              </article>
            )}

          </section>
        )}

        <div className="party-dashboard-nav-cards">
          {portalProfile?.permissions?.canViewOrders && (
            <button
              className="party-nav-card party-nav-card--sage"
              onClick={() => navigate("/orders")}
            >
              <span className="party-nav-card-icon">
                <TruckIcon />
              </span>
              <span className="party-nav-card-title">Order Status</span>
              <span className="party-nav-card-sub">
                Track portal and regular orders
              </span>
            </button>
          )}

          {portalProfile?.permissions?.canViewStock && (
            <button
              className="party-nav-card party-nav-card--gold"
              onClick={() => navigate("/stock")}
            >
              <span className="party-nav-card-icon">
                <SearchIcon />
              </span>
              <span className="party-nav-card-title">Stock Enquiry / Place Order</span>
              <span className="party-nav-card-sub">
                Check availability and place an order
              </span>
            </button>
          )}

        </div>

        <button
          type="button"
          className="dealer-dashboard-messages-link"
          onClick={() => navigate("/messages")}
        >
          <span className="dashboard-section-title">Messages</span>
          <small>Chat with your assigned salesperson and dispatch team</small>
          <span className="dashboard-card-arrow" aria-hidden="true">→</span>
        </button>
      </main>
      </div>
    </div>
  );
}

export default PartyDashboardPage;
